import { useEffect, useState } from "react";
import images from "@/assets/images";

// The cosmic eye image IS the orb. We apply state-reactive
// box-shadow / filter / transform around the image — never re-render
// the image's own composition. Respect prefers-reduced-motion.

// Each state maps to a shadow color/intensity. The image itself stays untouched
// outside of subtle filter swaps.
const STATE_STYLE = {
  idle: {
    shadow:
      "0 0 80px 10px hsla(41,80%,68%,0.45), 0 0 180px 40px hsla(41,70%,72%,0.25)",
    filter: "saturate(1) brightness(1)",
    anim: "anim-orb-img-breath",
  },
  connecting: {
    shadow:
      "0 0 70px 10px hsla(41,80%,68%,0.35), 0 0 160px 40px hsla(214,55%,75%,0.25)",
    filter: "saturate(0.95) brightness(0.95)",
    anim: "anim-orb-img-connecting",
  },
  listening: {
    shadow:
      "0 0 100px 16px hsla(41,90%,68%,0.65), 0 0 220px 60px hsla(41,80%,72%,0.35)",
    filter: "saturate(1.1) brightness(1.04)",
    anim: "anim-orb-img-listening",
  },
  processing: {
    shadow:
      "0 0 90px 14px hsla(260,55%,70%,0.42), 0 0 200px 50px hsla(230,55%,72%,0.30)",
    filter: "saturate(0.9) brightness(0.95) hue-rotate(-12deg)",
    anim: "anim-orb-img-thinking",
  },
  speaking: {
    shadow:
      "0 0 110px 18px hsla(41,95%,66%,0.75), 0 0 240px 70px hsla(41,90%,70%,0.4)",
    filter: "saturate(1.18) brightness(1.06)",
    anim: "anim-orb-img-speaking",
  },
  interrupted: {
    shadow:
      "0 0 80px 12px hsla(0,0%,100%,0.5), 0 0 160px 40px hsla(41,60%,80%,0.3)",
    filter: "saturate(1.2) brightness(1.15)",
    anim: "anim-orb-img-flash",
  },
  ended: {
    shadow:
      "0 0 60px 6px hsla(36,30%,75%,0.35), 0 0 140px 30px hsla(36,30%,80%,0.2)",
    filter: "saturate(0.85) brightness(0.95)",
    anim: "anim-orb-img-breath",
  },
  error: {
    shadow:
      "0 0 50px 6px hsla(0,0%,60%,0.35), 0 0 120px 30px hsla(0,0%,65%,0.2)",
    filter: "saturate(0.55) brightness(0.85) grayscale(0.4)",
    anim: "",
  },
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  return reduced;
}

export default function VoiceOrb({ callState, amplitude = 0 }) {
  const reduced = usePrefersReducedMotion();
  const cfg = STATE_STYLE[callState] || STATE_STYLE.idle;

  // Listening amplitude bumps the shadow intensity slightly (sync with fake mic level).
  const listeningBoost = callState === "listening" ? 1 + amplitude * 0.25 : 1;

  return (
    <div
      data-testid="voice-orb"
      data-state={callState}
      className="relative flex items-center justify-center"
      style={{
        width: "min(80vw, 280px)",
        height: "min(80vw, 280px)",
      }}
    >
      <div
        aria-hidden
        className={`absolute inset-0 rounded-[6%] ${reduced ? "" : cfg.anim}`}
        style={{
          boxShadow: cfg.shadow,
          transform: callState === "listening" && !reduced ? `scale(${1 + amplitude * 0.02})` : undefined,
          transition: "box-shadow 320ms ease-out, transform 140ms ease-out",
          opacity: listeningBoost > 1 ? 1 : 0.95,
        }}
      />
      <img
        src={images.callOrbEye}
        alt="Hands of golden light holding a radiant sphere"
        data-image-slot="call-orb-eye"
        data-testid="call-orb-eye-image"
        fetchPriority="high"
        draggable={false}
        className="relative z-10 h-full w-full select-none object-contain"
        style={{
          filter: cfg.filter,
          transition: "filter 420ms ease-out",
        }}
      />
      {/* desktop scaling */}
      <style>{`
        @media (min-width: 1024px) {
          [data-testid="voice-orb"] {
            width: min(40vw, 360px) !important;
            height: min(40vw, 360px) !important;
          }
        }
      `}</style>
    </div>
  );
}
